import { useNavigate } from 'react-router-dom';
import { useAppContext } from '@/contexts/AppContext.js';
import { LoadingState } from '@/components/common/LoadingState.js';
import { EmptyState } from '@/components/common/EmptyState.js';
import { Button } from '@/components/ui/button.js';
import { FileText, Search, Trash2 } from 'lucide-react';
import { cn, formatDate } from '@/lib/utils.js';

interface NotesGridViewProps {
  searchQuery: string;
  onNoteSelect?: (note: any) => void;
}

export function NotesGridView({ searchQuery, onNoteSelect }: NotesGridViewProps) {
  const { notes, notesLoading, openNote, deleteNote } = useAppContext();
  const navigate = useNavigate();

  const handleNoteClick = (note: typeof notes[0]) => {
    openNote(note);
    if (onNoteSelect) {
      onNoteSelect(note);
    } else {
      navigate('/notes/editor');
    }
  };

  const handleDelete = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    deleteNote(id);
  };

  const filteredNotes = notes.filter((note) =>
    note.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    note.content.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (notesLoading) {
    return <LoadingState message="Loading notes..." />;
  }

  return (
    <div className="flex flex-col h-full bg-background">
      <div className="flex-1 overflow-y-auto p-6">
        {filteredNotes.length === 0 && notes.length === 0 ? (
          <EmptyState
            icon={FileText}
            title="No notes yet"
            description="Click 'New Note' above to start capturing your thoughts, ideas, and important information."
          />
        ) : filteredNotes.length === 0 ? (
          <EmptyState
            icon={Search}
            title="No notes found"
            description="Try a different search term"
          />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {filteredNotes.map((note) => (
              <div
                key={note.id}
                onClick={() => handleNoteClick(note)}
                className={cn(
                  'group relative p-5 rounded-lg cursor-pointer',
                  'border border-border/50 bg-card',
                  'hover:border-primary/50 hover:shadow-md transition-all',
                  'flex flex-col h-48'
                )}
              >
                {/* Header */}
                <div className="flex items-start justify-between gap-2 mb-3">
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <FileText className="h-4 w-4 text-primary flex-shrink-0" />
                    <h3 className="font-semibold text-sm text-foreground truncate">
                      {note.title}
                    </h3>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
                    onClick={(e) => handleDelete(note.id, e)}
                  >
                    <Trash2 className="h-3.5 w-3.5 text-destructive" />
                  </Button>
                </div>

                {/* Content Preview */}
                <div className="flex-1 overflow-hidden mb-3">
                  {note.content ? (
                    <p className="text-xs text-muted-foreground line-clamp-5 leading-relaxed">
                      {note.content}
                    </p>
                  ) : (
                    <p className="text-xs text-muted-foreground/50 italic">
                      Empty note
                    </p>
                  )}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between pt-2 border-t border-border/50">
                  <p className="text-xs text-muted-foreground/70">
                    {formatDate(note.updatedAt)}
                  </p>
                  <div className="text-xs text-muted-foreground/50">
                    {note.content.length} chars
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
